import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import NewOrder from "./components/NewOrder";
import TrackRoute from "./components/TrackRoute";
import { MapControls } from "@/components/map";
import {
  DirectionsRenderer,
  GoogleMap,
  Marker,
  OverlayView,
  Polyline,
  TrafficLayer,
} from "@react-google-maps/api";
import Vehicles from "./components/Vehicles";
import useVehicles from "@/services/vehicles";
import GoogleMarkerStatus from "./components/GoogleMarkerStatus";
import { BASE_URL } from "@/config";
import { toast } from "sonner";
import { useGetList, usePermissions, useRefresh } from "react-admin";
import VehicleRoutes from "./components/VehicleRoutes";

import { Button } from "@/components/ui/button";
import { addHours, format, subHours } from "date-fns";
import { SoftPhone } from "@/libraries/softphone";
import { Car, Flag, Loader2, PersonStanding, Phone, Plus } from "lucide-react";
import { darkMapStyle, grayMapStyle, MD_WIDTH } from "@/utils/variables";
import { mergeDateAndTime } from "@/utils/formChange";
import MapLegend from "./components/MapLegend";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import { useUserContext } from "@/context/UserContext";
import { useSipContext } from "@/context/SipContext";
import { SipConnectionStatus } from "@/components/sip/SipConnectionStatus";
import { useSoftphoneContext } from "@/context/SoftphoneContext";
import { CallHistory } from "@/interface/dataInterface";
import axiosInstance from "@/utils/axiosInstance";
import { useLastCallStore } from "@/stores/lastCallStore";
import { SipCallManager } from "@/components/sip/SipCallManager";
import { convertHToMin } from "@/utils/generateTimeSlots";
import { Driver, Orders, Vehicle } from "@/interface/entityInterface";
import useCallHistory from "@/libraries/softphone/useCallHistory";
import { formatAddress } from "@/utils/functions";
import { useTheme } from "@/layout/ThemeProvider";
import { Badge } from "@/components/ui/badge";
import OrderMarker from "./components/OrderMarker";
import DispatchNav from "./components/Nav";
import ShowOrdersOnMap from "./components/ShowOrdersOnMap";
import ShowLegend from "./components/ShowLegend";
import NotificationsPopover from "@/layout/NotificationPopover";

const polylineOptions = {
  strokeColor: "#FF0000",
  strokeOpacity: 0.8,
  strokeWeight: 4,
  fillColor: "#FF0000",
  clickable: false,
  draggable: false,
  editable: false,
  geodesic: true,
};

interface Location {
  text: string;
  lat: number;
  lng: number;
}

const Dispatch: React.FC = () => {
  const {
    callHistory: callHistoryFromHook,
    isLoading: isLoadingCallHistoryFromHook,
    fetchCallHistory,
  } = useCallHistory();
  const refresh = useRefresh();
  const permissions = usePermissions();
  const { decodedToken } = useUserContext();
  const { theme } = useTheme();

  // Use new SIP implementation
  const {
    username,
    apiKey,
    recordingUrl,
    sipConfig: globalSipConfig,
    makeCall,
    hasFreePBX,
  } = useSipContext();
  const { lastCall, updateLastCallRecording } = useLastCallStore();

  // Lazy load call history - only fetch when History tab is clicked
  const [callHistory, setCallHistory] = useState<CallHistory[]>([]);
  const [isLoadingCallHistory, setIsLoadingCallHistory] = useState(false);

  // Sync call history from hook to local state
  useEffect(() => {
    if (callHistoryFromHook.length > 0) {
      setCallHistory(callHistoryFromHook);
    }
  }, [callHistoryFromHook]);

  // You can uncomment the debug below for troubleshooting
  // React.useEffect(() => {
  //   console.log('Call history debug:', {
  //     username,
  //     callHistoryLength: callHistory.length,
  //     callHistoryLoading,
  //     callHistoryError,
  //     sampleCallHistory: callHistory.slice(0, 2)
  //   });
  // }, [username, callHistory, callHistoryLoading, callHistoryError]);

  const [mapType, setMapType] = useState<string>("roadmap");
  const [isDark, setDark] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showOrdersOnMap, setShowOrders] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  const { isLoaded, loadError } = useGoogleMaps();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [isRouteOpen, setRouteOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = React.useState<any>(null);
  const [polylines, setPolylines] = React.useState<any>([]);
  const [orderId, setOrderId] = useState(0);

  const [currentPosition, setCurrentPosition] = React.useState<any>(null);
  const [sliderValue, setSliderValue] = React.useState(0);
  const [width] = useState(window.innerWidth);

  const { data, isLoading } = useVehicles();

  const [center, setCenter] = useState({
    lat: 41.3275,
    lng: 19.8189,
  });

  const [isNewOrderOpen, setNewOrderOpen] = useState(false);
  const [showVehicleList, setShowVehicleList] = useState(false);

  const [loading, setLoading] = React.useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<"all" | "active" | "parked">("all");
  const [vehicleId, setVehicleId] = useState(0);
  const [selectedVehicleList, setSelectedVehicleList] =
    useState<Vehicle | null>(null);

  const [commissionPartner, setCommissionPartner] = useState<any>(undefined);
  const now = useMemo(() => new Date(), []);
  const { data: orders = [] } = useGetList<Orders>("taxi-orders/map", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "pickup_time", order: "ASC" },
    filter: {
      status: ["NEW_ORDER", "ACCEPTED", "ASSIGNED", "GOING_TO_PICKUP"],
      startPickupTime: subHours(now, 1),
      endPickupTime: addHours(now, 3),
    },
  });

  const pickupInputRef = useRef<HTMLInputElement | null>(null);
  const dropoffInputRef = useRef<HTMLInputElement | null>(null);

  const [pickupLocation, setPickupLocation] = useState<Location>({
    text: "",
    lat: 0,
    lng: 0,
  });

  const [dropoffLocation, setDropoffLocation] = useState<Location>({
    text: "",
    lat: 0,
    lng: 0,
  });

  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    null,
  );

  const [inputLocationFocus, setFocus] = useState<"pickup" | "dropoff" | null>(
    null,
  );

  const [directions, setDirections] = useState<
    google.maps.DirectionsResult | undefined
  >(undefined);

  // Effect to draw trip when selectedRoute changes
  useEffect(() => {
    if (!selectedRoute) return;

    // Reset slider position for new route
    setSliderValue(0);
    // setLoadingDraw(true);
    setPolylines([]);
    setCurrentPosition(null);

    const url = `${BASE_URL}/gps-track/users/${selectedRoute.userId}/tracks/${format(selectedRoute.startTrackPoint.utc, "yyyy-MM-dd")}?startTime=${format(selectedRoute.startTrackPoint.utc, "HH:mm:ss")}&endTime=${format(selectedRoute.endTrackPoint.utc, "HH:mm:ss")}&filtered=true`;

    fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setPolylines(data);
        // setLoadingDraw(false);
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      });
  }, [selectedRoute]);

  const addOrder = async (data: any) => {
    setLoading(true);

    const dropoff = data.dropoffLocation?.text
      ? {
          dropoff: data.dropoffLocation.text,
          dropoff_latitude: data.dropoffLocation.lat,
          dropoff_longitude: data.dropoffLocation.lng,
        }
      : null;

    try {
      const response = await axiosInstance.post(`/taxi-orders`, {
        fixed_fee: true,
        pickup_time: mergeDateAndTime(data.date, data.time),
        status: "NEW_ORDER",
        pickup: data.pickupLocation.text,
        pickup_latitude: data.pickupLocation.lat,
        pickup_longitude: data.pickupLocation.lng,
        client_name: data.clientName,
        client_phone: data.phoneNumber,
        flight_number: data.flightNumber,
        auto_assign: false,
        notes: data.notes,
        fare: Number(data.fare),
        currency: data.currency,
        scheduled: data.scheduled,
        commission_partner_id:
          data.commission_partner_id || commissionPartner?.id || null,
        reminder_time: convertHToMin(data.hours, data.minutes) || null,
        source: data.source,
        ...dropoff,
      });

      if (
        selectedVehicleList &&
        Object.keys(selectedVehicleList).length > 0 &&
        selectedVehicleList.drivers?.length > 0
      ) {
        try {
          const availableDriver = selectedVehicleList.drivers.find(
            (driver: Driver) => driver.status === "AVAILABLE",
          );

          if (availableDriver) {
            const res = await axiosInstance.put(
              `/taxi-orders/assign-driver/${response.data.id}`,
              { driverId: availableDriver.id },
            );

            if (res.data) {
              toast(
                `Order added successfully and assigned to driver ${availableDriver.name}`,
              );
            }
          } else {
            toast.success(
              `Order added successfully with number ${response.data.id} (no available driver)`,
            );
          }
          refresh();
        } catch (error: any) {
          console.log(error);
          toast(error.response.data.message);
        } finally {
          setLoading(false);
        }
      } else {
        toast.success(
          `Order added successfully with number ${response.data.id}`,
        );
        refresh();
      }
    } catch (error: any) {
      toast.success(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (value: number[]) => {
    setSliderValue(value as unknown as number);
  };

  const closeListRoutes = useCallback(() => {
    setPolylines([]);
    setSelectedRoute(null);
  }, []);

  const selectedVehicleOnList = useCallback((vehicle: any) => {
    setSelectedVehicleList(vehicle);
    setSelectedVehicleId(vehicle?.id ?? null);
  }, []);

  const handleRouteChanged = useCallback((data: any) => {
    setSelectedRoute(data);
  }, []);

  const getVariableValue = useCallback((variables: any[], name: string) => {
    const found = variables.find((v) => v.name === name);
    return found?.value ?? null;
  }, []);

  const filteredVehicles = useMemo(() => {
    if (!data) return [];
    return data.filter((vehicle: any) => {
      const ignition = getVariableValue(vehicle.gpsData.variables, "Ignition");
      const matches = vehicle.nickname
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      if (!matches) return false;
      if (selected === "all") return true;
      if (selected === "active") return ignition === "True";
      if (selected === "parked") return ignition === "False";
      return true;
    });
  }, [data, searchTerm, selected, getVariableValue]);

  // Memoize the selected vehicle's GPS ID and nickname to prevent VehicleRoutes re-renders
  const selectedVehicleGpsId = useMemo(() => {
    return selectedVehicleList?.gpsData?.id;
  }, [selectedVehicleId]); // Only depend on the ID, not the whole object

  const selectedVehicleNickname = useMemo(() => {
    return selectedVehicleList?.nickname || selectedVehicleList?.license_plate;
  }, [selectedVehicleId]);

  useEffect(() => {
    if (theme == "system") {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches)
        setDark(true);
      else setDark(false);
    } else {
      if (theme === "dark") setDark(true);
      else setDark(false);
    }
  }, [theme]);

  useEffect(() => {
      if (!localStorage.getItem("showTraffic")) return;
      const isTraffic = JSON.parse(localStorage.getItem("showTraffic") || "");
      setShowTraffic(isTraffic);
    }, []);

  useEffect(() => {
    if (mapLoaded) {
      const directionsService = new window.google.maps.DirectionsService();

      if (pickupLocation.lat === 0 || dropoffLocation.lat === 0) {
        setDirections(undefined);
        return;
      }
      const request: google.maps.DirectionsRequest = {
        origin: new window.google.maps.LatLng(
          pickupLocation.lat,
          pickupLocation.lng,
        ),
        destination: new window.google.maps.LatLng(
          dropoffLocation.lat,
          dropoffLocation.lng,
        ),
        travelMode: window.google.maps.TravelMode.DRIVING,
      };
      directionsService.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && result) {
          setDirections(result);
        } else {
          console.error("Directions request failed:", status);
        }
      });
    }
  }, [mapLoaded, pickupLocation, dropoffLocation]);

  React.useEffect(() => {
    const index = Math.floor((sliderValue / 100) * (polylines?.length - 1));
    setCurrentPosition(polylines[index]);
  }, [sliderValue, polylines]);

  React.useEffect(() => {
    if (selectedVehicleList) {
      setSelectedVehicleList(null);
      setSelectedVehicleId(null);
    }
  }, [isNewOrderOpen]);

  useEffect(() => {
    if (!isNewOrderOpen) {
      if (polylines.length > 0) {
        setPolylines([]);
      }
      if (selectedRoute) {
        setSelectedRoute(null);
      }
      if (currentPosition) {
        setCurrentPosition(null);
      }
      if (directions) {
        setDirections(undefined);
      }
      if (pickupLocation.lat !== 0 || dropoffLocation.lat !== 0) {
        setPickupLocation({ text: "", lat: 0, lng: 0 });
        setDropoffLocation({ text: "", lat: 0, lng: 0 });
      }
    }
  }, [isNewOrderOpen]);

  useEffect(() => {
    if (selectedVehicleList) {
      map?.panTo({
        lat: selectedVehicleList.gpsData.position.latitude,
        lng: selectedVehicleList.gpsData.position.longitude,
      });
      map?.setZoom(17);
    }
  }, [selectedVehicleList, map]);

  useEffect(() => {
    if (map && polylines && polylines?.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      polylines?.forEach((coord: any) => {
        bounds.extend({
          lat: coord.position.latitude,
          lng: coord.position.longitude,
        });
      });
      map?.fitBounds(bounds);
    }
  }, [polylines]);

  useEffect(() => {
    if (
      decodedToken?.user &&
      permissions?.data?.user_role === "commission_partner"
    ) {
      // load commission partners if needed
      const loadCommissionPartners = async () => {
        try {
          const response = await axiosInstance.get(
            `/referral-agents?end=10000&start=1`,
          );
          if (response?.data) {
            const currentUserPartner = response.data.find(
              (partner: any) => partner.user_id === decodedToken.user.id,
            );
            setCommissionPartner(currentUserPartner);
          }
        } catch (error) {
          console.error("Error loading commission partners:", error);
        }
      };
      loadCommissionPartners();
    }
  }, [decodedToken]);

  const [softPhoneOpen, setSoftPhoneOpen] = useState(false);
  const {
    isOpen: globalSoftphoneOpen,
    closeSoftphone,
    toggleSoftphone,
  } = useSoftphoneContext();

  // Sync global softphone state with local state
  useEffect(() => {
    setSoftPhoneOpen(globalSoftphoneOpen);
  }, [globalSoftphoneOpen]);

  // Note: SIP configuration now comes from global SIP context
  // console.log('SIP Debug:', {
  //   globalSipConfig,
  //   isLoadingCredentials,
  //   credentialsError,
  //   username,
  //   softPhoneOpen
  // });

  // Settings state with localStorage persistence
  const [callVolume, setCallVolume] = useState(() => {
    const saved = localStorage.getItem("softphone-call-volume");
    return saved ? parseFloat(saved) : 0.8;
  });

  const [ringVolume, setRingVolume] = useState(() => {
    const saved = localStorage.getItem("softphone-ring-volume");
    return saved ? parseFloat(saved) : 0.6;
  });

  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem("softphone-notifications");
    return saved ? JSON.parse(saved) : true;
  });

  const [connectOnStart, setConnectOnStart] = useState(() => {
    const saved = localStorage.getItem("softphone-connect-on-start");
    return saved ? JSON.parse(saved) : false;
  });

  // Functions to save settings (implement localStorage if needed)
  const saveConnectOnStart = (value: boolean) => {
    setConnectOnStart(value);
    localStorage.setItem("softphone-connect-on-start", JSON.stringify(value));
  };

  const saveNotifications = (value: boolean) => {
    setNotifications(value);
    localStorage.setItem("softphone-notifications", JSON.stringify(value));
  };

  const saveCallVolume = (value: number) => {
    setCallVolume(value);
    localStorage.setItem("softphone-call-volume", value.toString());
  };

  const saveRingVolume = (value: number) => {
    setRingVolume(value);
    localStorage.setItem("softphone-ring-volume", value.toString());
  };

  // Handle call from softphone call history
  const handleCallFromHistory = (phoneNumber: string) => {
    console.log(`Making call to ${phoneNumber} from call history`);

    if (makeCall) {
      makeCall(phoneNumber);
    } else {
      console.error("makeCall function not available in SIP context");
      toast.error("Unable to make call");
    }
  };

  // Handle History tab click - fetch call history data lazily
  const handleHistoryTabClick = async () => {
    if (isLoadingCallHistory || isLoadingCallHistoryFromHook || !username)
      return; // Prevent duplicate requests

    setIsLoadingCallHistory(true);

    try {
      await fetchCallHistory();
    } catch (error) {
      console.error("Failed to load call history:", error);
      toast.error("Failed to load call history");
    } finally {
      setIsLoadingCallHistory(false);
    }
  };

  // Match last call with recording when call history is updated
  useEffect(() => {
    if (lastCall && callHistoryFromHook.length > 0) {
      // Find matching call in history (within 5 minutes of last call time)
      const lastCallTime = new Date(lastCall.time).getTime();
      const matchingCall = callHistoryFromHook.find(
        (historyCall: CallHistory) => {
          const historyCallTime = new Date(historyCall.calldate).getTime();
          const timeDiff = Math.abs(lastCallTime - historyCallTime);
          const phoneMatch =
            historyCall.cnum === lastCall.number ||
            historyCall.dst === lastCall.number;

          // Match if within 5 minutes and same phone number
          return timeDiff < 5 * 60 * 1000 && phoneMatch;
        },
      );

      if (matchingCall && matchingCall.recordingfile) {
        updateLastCallRecording(matchingCall.recordingfile);
      }
    }
  }, [callHistoryFromHook]);

  useEffect(() => {
    map?.setOptions({
      colorScheme: isDark ? "DARK" : "LIGHT",
    });
  }, [isDark]);

  useEffect(() => {
    if (orderId) {
      setShowOrders(true);
      setVehicleId(0);
    }
  }, [orderId]);

  useEffect(() => {
    if (!vehicleId) {
      setSelectedVehicleList(null);
      return;
    }
    const vehicle =
      filteredVehicles.find((item) => item.id === vehicleId) || null;
    setSelectedVehicleList(vehicle);
  }, [vehicleId]);

  // Helper to reverse geocode a lat/lng and set pickup/dropoff similar to dblclick behavior
  const handleMapLocationSelection = useCallback(
    (lat: number, lng: number) => {
      new google.maps.Geocoder().geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (results && status === "OK" && results[0]) {
            const place = {
              text: results[0].formatted_address,
              lat,
              lng,
            };
            if (
              pickupInputRef.current &&
              (inputLocationFocus === "pickup" || !inputLocationFocus)
            ) {
              setPickupLocation(place);
              pickupInputRef.current.value = place.text;
            } else if (
              dropoffInputRef.current &&
              inputLocationFocus === "dropoff"
            ) {
              setDropoffLocation(place);
              dropoffInputRef.current.value = place.text;
            }
            setCenter({ lat, lng });
          } else {
            console.log("No address found");
          }
        },
      );
      setNewOrderOpen(true);
    },
    [inputLocationFocus],
  );

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }

  return (
    <div className="h-full relative overflow-hidden">
      {/* Show SIP connection status - only if FreePBX is available */}
      {hasFreePBX && (
        <div
          className={`absolute top-16 min-[450px]:top-5 z-40 max-w-md ${isNewOrderOpen ? "left-115" : "left-66"}`}
        >
          <SipConnectionStatus variant="banner" />
        </div>
      )}

      <GoogleMap
        center={center}
        zoom={pickupLocation.text || dropoffLocation.text ? 18 : 11}
        mapContainerClassName="h-full"
        onLoad={(mapInstance: google.maps.Map) => {
          setMapLoaded(true);
          setMap(mapInstance);
        }}
        mapTypeId={
          mapType === "green" ? "roadmap" : (mapType as google.maps.MapTypeId)
        }
        options={{
          styles: isDark
            ? darkMapStyle
            : mapType === "green"
              ? null
              : grayMapStyle,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false, // we use our own control
          gestureHandling: "greedy",
          colorScheme: isDark ? "DARK" : "LIGHT",
        }}
        onDblClick={(data: any) => {
          const lat = data.latLng?.lat() || 0;
          const lng = data.latLng?.lng() || 0;
          new google.maps.Geocoder().geocode(
            { location: { lat, lng } },
            (results, status) => {
              if (results && status === "OK" && results[0]) {
                const formattedText = results[0].address_components
                  ? formatAddress(results[0].address_components)
                  : results[0].formatted_address;
                const place = {
                  text: formattedText,
                  lat,
                  lng,
                };
                if (
                  pickupInputRef.current &&
                  (inputLocationFocus === "pickup" || !inputLocationFocus)
                ) {
                  setPickupLocation(place);
                  pickupInputRef.current.value = place.text;
                } else if (
                  dropoffInputRef.current &&
                  inputLocationFocus === "dropoff"
                ) {
                  setDropoffLocation(place);
                  dropoffInputRef.current.value = place.text;
                }
                setCenter({ lat, lng });
              } else {
                console.log("No address found");
              }
            },
          );
          setNewOrderOpen(true);
          handleMapLocationSelection(lat, lng);
        }}
        // Handle single clicks on POIs (placeId present) and set locations the same as double-click
        onClick={(e: google.maps.MapMouseEvent) => {
          // If this click is on a place of interest, the event usually contains a placeId
          const placeId = (e as any).placeId;
          if (placeId) {
            // prevent the default info window from appearing
            try {
              (e as any).stop();
            } catch {
              // ignore if stop is not available
            }
            if (!isNewOrderOpen) return;
            const lat = e.latLng?.lat() || 0;
            const lng = e.latLng?.lng() || 0;
            handleMapLocationSelection(lat, lng);
          }
        }}
      >
        {showTraffic && <TrafficLayer />}
        {(pickupLocation.lat || pickupLocation.lng) && (
          <Marker
            draggable={true}
            onDragEnd={(data) => {
              const lat = data.latLng?.lat() || 0;
              const lng = data.latLng?.lng() || 0;
              new google.maps.Geocoder().geocode(
                { location: { lat, lng } },
                (results, status) => {
                  if (results && status === "OK" && results[0]) {
                    const formattedText = results[0].address_components
                      ? formatAddress(results[0].address_components)
                      : results[0].formatted_address;
                    const place = {
                      text: formattedText,
                      lat,
                      lng,
                    };
                    if (pickupInputRef.current) {
                      setPickupLocation(place);
                      setCenter({ lat, lng });
                      pickupInputRef.current.value = place.text;
                    }
                  } else {
                    console.log("No address found");
                  }
                },
              );
              setFocus("pickup");
            }}
            position={
              directions
                ? directions.routes[0].legs[0].start_location
                : { lat: pickupLocation.lat, lng: pickupLocation.lng }
            }
            label="S"
          />
        )}
        {showOrdersOnMap &&
          orders.length > 0 &&
          orders.map((order) => (
            <OrderMarker
              key={order.id}
              order={order}
              orderId={orderId}
              setOrderId={setOrderId}
              setVehicleId={setVehicleId}
            />
          ))}
        {(dropoffLocation.lat || dropoffLocation.lng) && (
          <Marker
            draggable={true}
            onDragEnd={(data) => {
              const lat = data.latLng?.lat() || 0;
              const lng = data.latLng?.lng() || 0;
              new google.maps.Geocoder().geocode(
                { location: { lat, lng } },
                (results, status) => {
                  if (results && status === "OK" && results[0]) {
                    const formattedText = results[0].address_components
                      ? formatAddress(results[0].address_components)
                      : results[0].formatted_address;
                    const place = {
                      text: formattedText,
                      lat,
                      lng,
                    };
                    if (dropoffInputRef.current) {
                      setDropoffLocation(place);
                      setCenter({ lat, lng });
                      dropoffInputRef.current.value = place.text;
                    }
                  } else {
                    console.log("No address found");
                  }
                },
              );
              setFocus("dropoff");
            }}
            position={
              directions
                ? directions.routes[0].legs[0].end_location
                : { lat: dropoffLocation.lat, lng: dropoffLocation.lng }
            }
            label="E"
          />
        )}
        {directions && (
          <DirectionsRenderer
            options={{
              draggable: false,
              suppressMarkers: true,
            }}
            directions={directions}
          />
        )}
        {polylines.length > 0 && (
          <Polyline
            path={polylines?.map((track: any) => {
              return {
                lat: track.position.latitude,
                lng: track.position.longitude,
              };
            })}
            options={polylineOptions}
          />
        )}
        {polylines.length > 0 && (
          <>
            <OverlayView
              position={{
                lat: polylines[0].position.latitude,
                lng: polylines[0].position.longitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center shadow-md">
                <Flag className="text-white w-4 h-4" />
              </div>
            </OverlayView>
            <OverlayView
              position={{
                lat: polylines[polylines.length - 1].position.latitude,
                lng: polylines[polylines.length - 1].position.longitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center shadow-md">
                <Flag className="text-white w-4 h-4" />
              </div>
            </OverlayView>
            {currentPosition && (
              <OverlayView
                position={{
                  lat: currentPosition.position.latitude,
                  lng: currentPosition.position.longitude,
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div className="w-9 h-9 bg-gray-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <Car className="text-white w-4 h-4" />
                </div>
              </OverlayView>
            )}
          </>
        )}
        {permissions && permissions.data && (
          <div>
            {filteredVehicles.map((item) => (
              <GoogleMarkerStatus
                key={item.id}
                data={item}
                selectedVehicleList={selectedVehicleList}
                setSelectedVehicleList={setSelectedVehicleList}
                setOrderId={setOrderId}
                setVehicleId={setVehicleId}
                setCenter={setCenter}
              />
            ))}
          </div>
        )}
      </GoogleMap>
      <div className="flex gap-x-2 items-start absolute top-5 left-5 lg:z-10">
        {permissions.data.settings_type == "taxi" && (
          <>
            {!isNewOrderOpen && (
              <Button
                onClick={() => {
                  if (width < MD_WIDTH) {
                    if (softPhoneOpen) toggleSoftphone();
                    setShowVehicleList(false);
                  }
                  setNewOrderOpen((prev: boolean) => !prev);
                }}
                className="relative"
              >
                <Plus size={16} />
                New Order
              </Button>
            )}
            {isNewOrderOpen && (
              <NewOrder
                ordersData={addOrder}
                pickupLocation={pickupLocation}
                setPickupLocation={setPickupLocation}
                dropoffLocation={dropoffLocation}
                setDropoffLocation={setDropoffLocation}
                isNewOrderOpen={isNewOrderOpen}
                setNewOrderOpen={setNewOrderOpen}
                isLoading={loading}
                selectedVehicle={selectedVehicleList}
                pickupLocationRef={pickupInputRef}
                dropoffLocationRef={dropoffInputRef}
                setCenter={setCenter}
                setFocus={setFocus}
              />
            )}
          </>
        )}
      </div>
      <div
        className={`flex flex-col gap-y-2 items-start absolute top-16 lg:z-1 min-[450px]:top-5 ${isNewOrderOpen && width >= MD_WIDTH ? "left-96" : "left-5 min-[450px]:left-37"}`}
      >
        {/* Only show softphone components if FreePBX is available */}
        {hasFreePBX && !softPhoneOpen && <SipCallManager />}

        {hasFreePBX && (
          <div className="flex gap-x-2 items-start">
            {/* Built-in Launcher Button */}
            <Button
              onClick={() => {
                if (width <= MD_WIDTH) {
                  setNewOrderOpen(false);
                  setShowVehicleList(false);
                }
                toggleSoftphone();
              }}
              className="relative"
              variant="outline"
            >
              <Phone size={16} />
              Softphone
            </Button>
          </div>
        )}

        {/* SoftPhone displayed below when opened */}
        {hasFreePBX && softPhoneOpen && globalSipConfig && (
          <SoftPhone
            // Visibility control
            softPhoneOpen={softPhoneOpen}
            setSoftPhoneOpen={closeSoftphone}
            // Audio settings
            callVolume={callVolume}
            ringVolume={ringVolume}
            // Connection settings
            connectOnStart={connectOnStart}
            notifications={notifications}
            // SIP configuration will be set below

            // Settings callbacks
            setConnectOnStartToLocalStorage={saveConnectOnStart}
            setNotifications={saveNotifications}
            setCallVolume={saveCallVolume}
            setRingVolume={saveRingVolume}
            // Optional: Built-in floating launcher
            builtInLauncher={false}
            // Optional: Accounts for call transfer
            //asteriskAccounts={[]}

            // Optional: Timezone for call history
            timelocale="Europe/Tirane"
            // New: Call history integration
            oldCallHistory={callHistory}
            onCallNumber={handleCallFromHistory}
            onHistoryTabClick={handleHistoryTabClick}
            recording_url={recordingUrl}
            apiKey={apiKey}
            config={globalSipConfig}
          />
        )}
      </div>
      <div
        className={"h-[40px] mx-auto absolute bottom-4 left-5 max-w-[40px] z-10"}
      >
        <div className="flex items-start justify-start">
          <DispatchNav
            components={[
              {
                key: "orders",
                Component: (
                  <ShowOrdersOnMap
                    showOrders={showOrdersOnMap}
                    setShowOrders={setShowOrders}
                    ordersLength={orders.length}
                  />
                ),
              },
              {
                key: "map",
                Component: (
                  <MapControls
                    mapType={mapType}
                    showTraffic={showTraffic}
                    onMapTypeChange={setMapType}
                    onTrafficToggle={setShowTraffic}
                  />
                ),
              },
              {
                key: 'legend',
                Component: (
                  <ShowLegend 
                    showLegend={showLegend}
                    setShowLegend={setShowLegend}
                  />
                )
              }
            ]}
            mainComponent={<NotificationsPopover />}
          />
        </div>
      </div>
      {permissions && permissions.data && (
        <div>
          {
            <div className="absolute top-5 right-5">
              {!showVehicleList && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      if (width < MD_WIDTH) {
                        setNewOrderOpen(false);
                        if (softPhoneOpen) toggleSoftphone();
                      }
                      setShowVehicleList(true);
                    }}
                    type="button"
                    className="relative cursor-pointer"
                  >
                    Vehicles
                    {isLoading ? (
                      <div className="flex items-center justify-center h-13">
                        <Loader2 className="animate-spin text-gray-500" />
                      </div>
                    ) : (
                      <Badge variant="secondary">{data?.length}</Badge>
                    )}
                  </Button>
                </div>
              )}
              {showVehicleList && (
                <Vehicles
                  data={data}
                  isLoading={isLoading}
                  textSearch={setSearchTerm}
                  filterStatus={setSelected}
                  setSelectedVehicleOnList={selectedVehicleOnList}
                  selectedVehicleList={undefined}
                  setRouteOpen={setRouteOpen}
                  setShowList={setShowVehicleList}
                />
              )}
            </div>
          }
        </div>
      )}
      {permissions &&
        permissions.data &&
        selectedVehicleList &&
        isRouteOpen && (
          <>
            <div className={`absolute top-5 right-5`}>
              <VehicleRoutes
                vehicleGpsId={selectedVehicleGpsId!}
                vehicleNickname={selectedVehicleNickname!}
                onRouteChanged={handleRouteChanged}
                closeListRoutes={closeListRoutes}
                isRouteOpen={isRouteOpen}
                setRouteOpen={setRouteOpen}
              />
            </div>
            {selectedRoute && (
              <TrackRoute
                currentPosition={currentPosition}
                onSliderChange={handleSliderChange}
                sliderValue={sliderValue}
              />
            )}
          </>
        )}
      {decodedToken?.settings.settings_type === "taxi" && showLegend && (
        <div className="absolute bottom-5 left-[55%] lg:left-1/3 flex">
          <MapLegend />
        </div>
      )}
    </div>
  );
};

export default Dispatch;
